import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useSubmitLinkpayPayment from "../../../apis/useSubmitLinkpayPayment/useSubmitLinkpayPayment";
import { useLinkPayCookies } from "../../../common/hooks/useLinkPayCookies";
import Condition from "../../../components/Conditional/Condition";
import Overlay from "../../../components/Overlay/Overlay";
import { Button } from "../../../components/elements";
import PopupDialog from "../../../components/elements/PopupDialog/PopupDialog";
import { useCartActions } from "../../../contexts/CartServiceContext/CartServiceContext";
import { usePaymentContext } from "../../../contexts/PaymentContext/PaymentContext";
import { PATH } from "../../../router/path";
import { LINK_PAY_SUCCESS_CODE } from "../../constants";
import "./LinkpayCheckoutForm.scss";

const LinkpayCheckoutForm: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { submitLinkpayPayment } = useSubmitLinkpayPayment();
	const [isProcessing, setIsProcessing] = useState(false);

	// Get LinkPay cookies
	const { accessToken: fpPayToken, uid: fpUid } = useLinkPayCookies();
	const [showErrorPopup, setShowErrorPopup] = useState(false);
	const [isTimeoutError, setIsTimeoutError] = useState(false);
	const { state: paymentReference } = usePaymentContext((state) => state?.paymentReference);
	const { CLEAN_CART } = useCartActions();

	const handleFPPayClick = useCallback(async () => {
		// Reset states
		setIsTimeoutError(false);
		setShowErrorPopup(false);

		// Validate tokens exist before proceeding
		if (!fpPayToken || !fpUid) {
			setIsTimeoutError(false);
			setShowErrorPopup(true);

			return;
		}

		setIsProcessing(true);

		try {
			const result = await submitLinkpayPayment(fpPayToken, fpUid);

			setIsProcessing(false);

			// Check if payment was successful
			if (result?.status === LINK_PAY_SUCCESS_CODE) {
				CLEAN_CART();
				navigate(`${PATH.ORDER_CONFIRMATION}?merchantReference=${paymentReference}`, {
					replace: false,
				});
			} else {
				// For non-success responses, show the standard error
				setIsTimeoutError(false);
				setShowErrorPopup(true);
			}
		} catch (err: any) {
			setIsProcessing(false);
			console.error("Linkpay payment error:", err);

			// Check if it's a timeout error
			const isTimeout =
				err?.code === "ECONNABORTED" ||
				err?.code === "ETIMEDOUT" ||
				err?.message?.toLowerCase().includes("timeout") ||
				err?.response?.status === 408;

			setIsTimeoutError(isTimeout);
			setShowErrorPopup(true);
		}
	}, [submitLinkpayPayment, fpPayToken, fpUid, paymentReference, navigate, CLEAN_CART]);

	const handleCloseError = useCallback(() => {
		setShowErrorPopup(false);
		setIsProcessing(false);
		navigate(PATH.CART);
	}, [navigate]);

	const handleTryAgain = useCallback(() => {
		setShowErrorPopup(false);
		setIsProcessing(false);
		handleFPPayClick();
	}, [handleFPPayClick]);

	// Memoize popup title and message
	const popupTitle = useMemo(() => {
		return isTimeoutError
			? t("paymentPage.linkpay.timeoutTitle")
			: t("paymentPage.linkpay.failedTitle");
	}, [isTimeoutError, t]);

	const popupMessage = useMemo(() => {
		return isTimeoutError
			? t("paymentPage.linkpay.timeoutMessage")
			: t("paymentPage.linkpay.failedMessage");
	}, [isTimeoutError, t]);

	// Memoize popup buttons to prevent recreation on every render
	const popupButtons = useMemo(() => {
		return isTimeoutError
			? [
					{
						text: t("paymentPage.linkpay.tryAgain"),
						onClick: handleTryAgain,
						className: "PrimaryButton",
					},
				]
			: [
					{
						text: t("paymentPage.linkpay.close"),
						onClick: handleCloseError,
						className: "PrimaryButton",
					},
				];
	}, [isTimeoutError, t, handleTryAgain, handleCloseError]);

	// Memoize payment info text
	const paymentInfoText = useMemo(() => {
		return t("paymentPage.linkpay.paymentInfo");
	}, [t]);

	return (
		<div className="LinkpayCheckoutPage">
			<Condition check={isProcessing}>
				<Overlay
					data-testid="linkpay-processing-overlay"
					line1={t("paymentPage.linkpay.processingPayment")}
					line2={t("paymentPage.linkpay.dontClosePage")}
				/>
			</Condition>
			<div className="FPPayText">{paymentInfoText}</div>
			<Button
				className="FPPayButton"
				onClick={handleFPPayClick}
				data-testid="fppay-button"
				disabled={isProcessing}
				disableElevation
			>
				{t("paymentPage.linkpay.confirmPay")}
			</Button>

			<PopupDialog
				data-testid="linkpay-error-popup"
				open={showErrorPopup}
				title={popupTitle}
				onClose={handleCloseError}
				content={<div className="ErrorContent">{popupMessage}</div>}
				buttons={popupButtons}
				className="LinkpayErrorPopup"
			/>
		</div>
	);
};

export default LinkpayCheckoutForm;
